/**
 * Error details that can be attached to API responses
 * Contains request context information for debugging
 */
export interface ApiErrorDetails {
    /** HTTP status code */
    status?: number;
    /** Request URL */
    url?: string;
    /** HTTP method */
    method?: string;
    /** Nested error details from downstream services */
    details?: ApiErrorDetails;
    /** Additional arbitrary details */
    [key: string]: unknown;
}

/**
 * Represents a successful API response body.
 */
export interface ApiSuccessResponseBody<T> {
    ok: true;
    data: T;
}

/**
 * Represents a failed API response body.
 */
export interface ApiErrorResponseBody {
    ok: false;
    error: string;
    details?: ApiErrorDetails;
}

/**
 * API response body using discriminated union pattern.
 * Check `body.ok` to narrow the type.
 */
export type ApiResponseBody<T> =
    | ApiSuccessResponseBody<T>
    | ApiErrorResponseBody;

/**
 * Complete API response including HTTP metadata.
 *
 * @example
 * ```typescript
 * const response = await client.getData();
 *
 * // Check success and access data
 * if (response.body.ok) {
 *   console.log(response.body.data);
 * } else {
 *   console.error(response.body.error);
 * }
 *
 * // Or use getDataOrThrow for simpler code
 * const data = getDataOrThrow(response);
 * ```
 */
export interface ApiResponse<T> {
    /** HTTP response headers from upstream API */
    headers?: Headers;
    /** HTTP status code */
    status: number;
    /** Whether HTTP request succeeded (status 2xx) */
    ok: boolean;
    /** The response body */
    body: ApiResponseBody<T>;
}

/**
 * Options for success() and failure() helper functions.
 */
export interface ApiResponseOptions {
    headers?: Headers;
    status?: number;
    details?: ApiErrorDetails;
}

/**
 * Extracts data from an ApiResponse, throwing an error if the response failed.
 *
 * @example
 * ```typescript
 * const response = await client.getUsers();
 * const users = getDataOrThrow(response); // throws if failed, returns data if success
 * ```
 */
export function getDataOrThrow<T>(response: ApiResponse<T>): T {
    if (!response.body.ok) {
        throw new Error(response.body.error);
    }
    return response.body.data;
}
